import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { useContext, useMemo, useState } from "react";
import { cardComponentRegistry } from "@/components/shared/form-parsers/cardRegistry";
import { TopBar } from "@/components/shared/TopBar";
import { Button } from "@stackje/shared-components/ui/button";
import { ChevronLeft, XIcon, PaletteIcon } from "lucide-react";
import { CardTypeSelector } from "./CardTypeSelector";
import { AddCardContent } from "./AddCardContent";
import { UnsavedChangesDialog } from "../shared/UnsavedChangesDialog";
import { ColorSchemeContext } from "@/context/ColorSchemeContext";
import { ColorScheme } from "@stackje/common/colorScheme";
import { CommonColorSchema } from "@stackje/common/config";
import { generateNewColors } from "@/utils/color-scheme-utils";
import { useColorSchemeShuffler } from "@/hooks/useColorSchemeShuffler";

interface AddCardPageProps {
  type: string;
  onSubmit?: (formData: FormData) => Promise<void>;
}

export default function AddCardPage({ type, onSubmit }: AddCardPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"style" | "form">("style");
  const [actionData, ] = useState<{
    validationErrors?: string[];
  } | null>(null);
  const { hasUnsavedChanges, markAsDirty } = useUnsavedChanges();
  const { colorSchemeId, customColors } = useContext(ColorSchemeContext)
  const currentSchemeColors = useMemo(() => {
    // Use custom colors if available, otherwise use predefined scheme colors
    return colorSchemeId === "custom" && customColors
      ? customColors
      : ColorScheme[colorSchemeId];
  }, [colorSchemeId, customColors]);
  const defaultColors = useMemo(() => {
    return CommonColorSchema.parse({});
  }, []);
  const generatedColors = useMemo(() => {
    const colors = generateNewColors(defaultColors, currentSchemeColors);
    return colors;
  }, [defaultColors, currentSchemeColors]);
  
  const { colors, shuffleColors } = useColorSchemeShuffler(generatedColors);
  
  const handleBackClick = () => {
    history.back();
  };

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    await onSubmit?.(formData);
    setIsSubmitting(false);
  };

  const cardConfig = cardComponentRegistry[type!];
  if (!cardConfig) return <div>Unsupported card type</div>;

  const {
    formContent,
    previewContent,
    toggleType,
    shuffleColors: formShuffleColors,
    handleSubmit: handleSubmitCardConfig,
  } = cardConfig.getComponents(colors);

  const title =
    step === "style"
      ? "Choose Style"
      : step === "form"
        ? "Edit Content"
        : "Preview";

  if (step === "style") {
    return (
      <div className="mx-auto mt-14 lg:mt-16 flex flex-col items-center justify-center">
        <TopBar>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => history.back()}
            className="text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-center">{title}</h1>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Shuffle colors"
            onClick={shuffleColors}
            className="text-gray-600 hover:text-gray-900"
          >
            <PaletteIcon className="h-5 w-5" />
          </Button>
        </TopBar>
        <div className="container mx-auto">
          <CardTypeSelector
            cardType={type!}
            onSelect={(subtype) => {
              toggleType?.(subtype);
              setStep("form");
            }}
            colors={colors}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pt-6 lg:pt-10">
      <TopBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="text-gray-600 hover:text-gray-900"
        >
          <XIcon className="h-5 w-5" />
        </Button>

        <h1 className="text-center">{title}</h1>

        <div className="w-8"></div>
      </TopBar>

      <div className="w-full mt-8">
        <div className="container mx-auto">
          {actionData?.validationErrors && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="text-red-800 font-medium mb-2">
                Validation Errors:
              </h3>
              <ul className="list-disc list-inside text-red-700">
                {actionData.validationErrors.map(
                  (error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ),
                )}
              </ul>
            </div>
          )}
          <AddCardContent
            previewContent={previewContent}
            formContent={formContent}
            switchSubTypeSelection={toggleType}
            switchThemeSelection={formShuffleColors}
            onFormInputChange={markAsDirty}
            onSubmit={handleSubmit}
            handleSubmit={handleSubmitCardConfig}
          />
        </div>
      </div>

      <UnsavedChangesDialog
        isDirty={hasUnsavedChanges}
        disabled={isSubmitting}
      />
    </div>
  );
}
